/**
 * NATURAL SEARCH — the single search brain for every GIGGONE search box.
 *
 * WHY: the old boxes did a raw `haystack.includes(needle)` on the rows that
 * happened to be loaded. That broke the way people actually search:
 *   - "California" never matched, because listings store the state as "CA";
 *   - "CA" matched "cabinet", "carpet", "decals"… anything containing "ca";
 *   - "electrician" never matched the `electrical` trade;
 *   - and the marketplace only searched the first 60 rows in the browser, so
 *     even a correct match outside that page was invisible.
 *
 * HOW IT WORKS
 *   parseSearch("roofing in Fontana CA") ->
 *     [ trade term "roofing" -> {roofing},
 *       text  term "fontana",
 *       state term "ca"      -> {CA} ]           ("in" is a filler word)
 *
 *   matchesSearch(listing, parsed):
 *     - STATE terms:  listing.state is one of the states        (OR'd together)
 *     - TRADE terms:  listing.trade is one of the trades, or the
 *                     word appears in the listing text          (OR'd together)
 *     - TEXT terms:   every word must start a word somewhere in
 *                     title / description / city / trade label   (AND)
 *
 * Server side: `serverFilters()` reduces the parsed terms to
 * { states, trades, laCity, textTerms }. That ONE object is applied as SQL by
 * the admin consoles (src/lib/adminSearch.ts) and by the `browse-listings`
 * edge function (body.search), so "California" returns every California
 * project, not only the ones on the first page. `matchesSearch` below is only
 * for rows already in memory (My Leads, offline fallback).
 *
 * Privacy: the matcher only ever sees what the caller passes in. The
 * marketplace passes public, already-redacted rows; My Leads passes the
 * buyer's own unlocked contacts. Nothing here fetches data.
 */
import { TRADES, Lang, tradeLabel } from '@/data/constants';
import { STATES } from '@/data/seo/states';

/** Lower-case, accent-free, punctuation-as-space, single-spaced. */
export const normalizeText = (s: unknown): string =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[-_/,.()'"&+]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/* ------------------------------------------------------------------ */
/* STATES                                                              */
/* ------------------------------------------------------------------ */

/** normalized name / nickname -> postal code ("california" -> "CA", "nueva york" -> "NY"). */
const STATE_ALIASES = new Map<string, string>();
STATES.forEach((s) => {
  STATE_ALIASES.set(normalizeText(s.en), s.code);
  STATE_ALIASES.set(normalizeText(s.es), s.code);
});
[
  ['cali', 'CA'], ['socal', 'CA'], ['norcal', 'CA'], ['southern california', 'CA'], ['northern california', 'CA'],
  ['inland empire', 'CA'], ['district of columbia', 'DC'], ['washington dc', 'DC'], ['d c', 'DC'],
  ['nueva mexico', 'NM'], ['hawai', 'HI'], ['pennsylvania', 'PA'], ['tejas', 'TX'], ['florida', 'FL'],
].forEach(([k, v]) => STATE_ALIASES.set(k, v));

const STATE_CODES = new Set(STATES.map((s) => s.code.toLowerCase()));

/**
 * Postal codes that are ALSO everyday words in English or Spanish. Typed in
 * lower case inside a longer query ("roofing in fontana", "pintura de la casa")
 * they are filler, not a state. Typed in CAPITALS ("roofing IN") or on their
 * own ("pa") they are read as the state.
 */
const AMBIGUOUS_CODES = new Set(['in', 'or', 'me', 'hi', 'ok', 'oh', 'de', 'la', 'al', 'co', 'pa', 'ma', 'id', 'mo', 'ne', 'ga', 'mi', 'va']);

/** Words that carry no search meaning on their own. */
const FILLER = new Set([
  'in', 'near', 'at', 'the', 'a', 'an', 'for', 'and', 'or', 'of', 'to', 'my', 'me', 'around', 'area', 'city', 'state',
  'job', 'jobs', 'project', 'projects', 'lead', 'leads', 'work', 'service', 'services', 'company', 'companies',
  'contractor', 'contractors', 'pro', 'pros', 'find', 'need', 'looking', 'best', 'local',
  'de', 'del', 'en', 'la', 'el', 'los', 'las', 'y', 'o', 'cerca', 'para', 'un', 'una', 'mi',
  'trabajo', 'trabajos', 'proyecto', 'proyectos', 'contratista', 'contratistas', 'servicio', 'servicios', 'empresa',
]);

/* ------------------------------------------------------------------ */
/* TRADES                                                              */
/* ------------------------------------------------------------------ */

/** Everyday words people type that are not in the category label itself. */
const TRADE_SYNONYMS: Record<string, string[]> = {
  electrical: ['electrician', 'electricians', 'electricista', 'electric', 'wiring', 'electrico', 'panel upgrade'],
  plumbing: ['plumber', 'plumbers', 'plomero', 'plomeros', 'fontanero', 'pipes', 'leak', 'drain', 'sewer'],
  painting: ['painter', 'painters', 'pintor', 'pintores', 'paint'],
  roofing: ['roofer', 'roofers', 'roof', 'roofs', 'techador', 'techo', 'techos', 'techero', 'shingles'],
  hvac: ['ac', 'a c', 'air conditioning', 'air conditioner', 'heating', 'cooling', 'furnace', 'heat pump', 'aire acondicionado', 'calefaccion', 'mini split', 'minisplit'],
  landscaping: ['landscaper', 'landscapers', 'gardener', 'gardening', 'jardinero', 'yard'],
  flooring: ['floor', 'floors', 'tile', 'hardwood', 'laminate', 'vinyl', 'piso'],
  concrete: ['cement', 'driveway', 'patio', 'cemento', 'slab', 'masonry'],
  'general-construction': ['general contractor', 'gc', 'remodeling', 'construction', 'addition', 'adu', 'builder'],
  drywall: ['sheetrock', 'drywaller'],
  framing: ['framer', 'carpentry', 'carpenter', 'carpintero'],
  windows: ['window'],
  doors: ['door'],
  solar: ['solar panels', 'solar panel', 'paneles solares'],
  handyman: ['handy man', 'repairs', 'odd jobs', 'maintenance man'],
  'house-cleaning': ['maid', 'maids', 'housekeeping', 'housekeeper', 'cleaner', 'cleaners', 'house cleaner'],
  'pool-cleaning': ['pool service', 'pool guy'],
  'pest-control': ['exterminator', 'termite', 'termites', 'fumigation', 'fumigacion', 'bugs'],
  'lawn-care': ['lawn', 'mowing', 'grass', 'yard work'],
  'tree-trimming': ['tree service', 'arborist', 'tree removal'],
  'junk-removal': ['hauling', 'haul away', 'trash removal'],
  'appliance-repair': ['washer', 'refrigerator', 'fridge', 'dishwasher', 'oven'],
  'water-heater-repair': ['tankless'],
  'ev-charger-installation': ['ev', 'tesla charger', 'charger', 'car charger'],
  'security-cameras': ['camera', 'cctv'],
  'water-damage': ['flood', 'flooding'],
  'kitchen-remodel': ['kitchen'],
  'bathroom-remodel': ['bathroom', 'bath', 'shower'],
  'fence-repair': ['fence', 'fencing'],
  'smart-home': ['smart home'],
};

/** Label words too generic to map to a category on their own. */
const GENERIC_LABEL_WORDS = new Set(['services', 'servicios', 'installation', 'instalacion', 'general', 'control']);

/** normalized phrase -> set of trade slugs. */
const TRADE_ALIASES = new Map<string, Set<string>>();
const addTradeAlias = (phrase: string, slug: string) => {
  const key = normalizeText(phrase);
  if (!key) return;
  if (!TRADE_ALIASES.has(key)) TRADE_ALIASES.set(key, new Set());
  TRADE_ALIASES.get(key)!.add(slug);
};
TRADES.forEach((t) => {
  [t.slug, t.en, t.es, t.esSlug].forEach((p) => addTradeAlias(p, t.slug));
  // Individual label words ("cleaning", "limpieza", "repair", "remodel") match
  // every category that contains them.
  `${normalizeText(t.en)} ${normalizeText(t.es)}`.split(' ').forEach((w) => {
    if (w.length >= 5 && !GENERIC_LABEL_WORDS.has(w) && !FILLER.has(w)) addTradeAlias(w, t.slug);
  });
});
Object.entries(TRADE_SYNONYMS).forEach(([slug, words]) => words.forEach((w) => addTradeAlias(w, slug)));

const MAX_PHRASE = 4;

/* ------------------------------------------------------------------ */
/* PARSE                                                               */
/* ------------------------------------------------------------------ */

export type SearchTermKind = 'state' | 'trade' | 'text';

export interface SearchTerm {
  kind: SearchTermKind;
  /** normalized words of the term, e.g. "san bernardino" */
  text: string;
  /** postal codes, for kind === 'state' */
  states: string[];
  /** trade slugs, for kind === 'trade' */
  trades: string[];
}

export interface ParsedSearch {
  raw: string;
  terms: SearchTerm[];
  empty: boolean;
}

export const parseSearch = (raw: string): ParsedSearch => {
  const rawTokens = String(raw || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[-_/,.()'"&+]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .slice(0, 12);
  const tokens = rawTokens.map((t) => t.toLowerCase());
  const single = tokens.length === 1;
  const terms: SearchTerm[] = [];

  let i = 0;
  while (i < tokens.length) {
    let consumed = 0;

    // Longest phrase first: "new york", "garage door", "air conditioning".
    for (let n = Math.min(MAX_PHRASE, tokens.length - i); n >= 1 && !consumed; n--) {
      const phrase = tokens.slice(i, i + n).join(' ');
      if (n === 1 && phrase.length === 2 && STATE_CODES.has(phrase)) {
        const upper = rawTokens[i] === rawTokens[i].toUpperCase();
        if (!AMBIGUOUS_CODES.has(phrase) || upper || single) {
          terms.push({ kind: 'state', text: phrase, states: [phrase.toUpperCase()], trades: [] });
          consumed = 1;
          break;
        }
        continue; // lower-case filler like "in" / "de" / "la"
      }
      const state = STATE_ALIASES.get(phrase);
      if (state) {
        terms.push({ kind: 'state', text: phrase, states: [state], trades: [] });
        consumed = n;
        break;
      }
      const trades = TRADE_ALIASES.get(phrase) || (phrase.length > 4 && phrase.endsWith('s') ? TRADE_ALIASES.get(phrase.slice(0, -1)) : undefined);
      if (trades && !(n === 1 && FILLER.has(phrase) && !single)) {
        terms.push({ kind: 'trade', text: phrase, states: [], trades: [...trades] });
        consumed = n;
        break;
      }
    }

    if (!consumed) {
      const word = tokens[i];
      if (!(FILLER.has(word) && !single) && !(word.length === 2 && STATE_CODES.has(word) && !single)) {
        terms.push({ kind: 'text', text: word, states: [], trades: [] });
      }
      consumed = 1;
    }
    i += consumed;
  }

  return { raw, terms, empty: terms.length === 0 };
};

/* ------------------------------------------------------------------ */
/* MATCH                                                               */
/* ------------------------------------------------------------------ */

/** True when `term` starts a word (or a run of words) in the haystack. Short terms must be a whole word. */
export const textHits = (hay: string, words: string[], term: string): boolean => {
  if (!term) return true;
  if (term.includes(' ')) return ` ${hay} `.includes(` ${term}`);
  if (term.length < 3) return words.includes(term);
  if (words.some((w) => w.startsWith(term))) return true;
  // simple plural tolerance: "fontanas" / "roofs"
  return term.length > 4 && term.endsWith('s') && words.some((w) => w.startsWith(term.slice(0, -1)));
};

const toStateCode = (v: unknown): string => {
  const s = String(v ?? '').trim();
  if (!s) return '';
  if (s.length === 2) return s.toUpperCase();
  return STATE_ALIASES.get(normalizeText(s)) || s.toUpperCase();
};

export interface SearchableRow {
  title?: string | null;
  description?: string | null;
  city?: string | null;
  state?: string | null;
  trade?: string | null;
  project_type?: string | null;
  budget_range?: string | null;
}

/**
 * Does one row satisfy the parsed search?
 * `extra` adds already-visible strings (localized trade label, a buyer's own
 * contact name/phone in My Leads, a poster email for admins).
 */
export const matchesSearch = (
  row: SearchableRow,
  parsed: ParsedSearch | string,
  extra: Array<string | null | undefined> = [],
): boolean => {
  const p = typeof parsed === 'string' ? parseSearch(parsed) : parsed;
  if (p.empty) return true;

  const trade = String(row.trade || '');
  const hay = normalizeText(
    [
      row.title, row.description, row.city, trade,
      trade ? tradeLabel(trade, 'en') : '', trade ? tradeLabel(trade, 'es') : '',
      row.project_type, row.budget_range, ...extra,
    ]
      .filter(Boolean)
      .join(' '),
  );
  const words = hay.split(' ');
  const state = toStateCode(row.state);
  const city = normalizeText(row.city);

  const stateTerms = p.terms.filter((t) => t.kind === 'state');
  if (stateTerms.length && !stateTerms.some((t) => t.states.includes(state) || (t.text === 'la' && city.includes('los angeles')))) {
    return false;
  }
  const tradeTerms = p.terms.filter((t) => t.kind === 'trade');
  if (tradeTerms.length && !tradeTerms.some((t) => t.trades.includes(trade) || textHits(hay, words, t.text))) {
    return false;
  }
  return p.terms.filter((t) => t.kind === 'text').every((t) => textHits(hay, words, t.text));
};

/**
 * Column-level filters — THE server-side matching rule. Used by the admin
 * consoles (src/lib/adminSearch.ts -> PostgREST) AND sent as `body.search` to
 * the `browse-listings` edge function, which turns the same object into SQL.
 *
 *   states     -> `.eq('state', X)` / `.in('state', [...])`   (state terms OR'd)
 *   trades     -> `.eq('trade', X)` / `.in('trade', [...])`   (trade terms OR'd)
 *   laCity     -> the bare word "LA" also means the city of Los Angeles,
 *                 mirroring the rule in `matchesSearch`
 *   textTerms  -> every word must appear somewhere (AND), sanitised to
 *                 [a-z0-9] so it can never break PostgREST `or=()` syntax
 *
 * Trade terms are applied STRICTLY to the trade column here (no description
 * fallback): "roofing" means the roofing category, and an indexed equality
 * filter keeps the whole-table query fast.
 */
export interface ServerFilters {
  states: string[];
  trades: string[];
  laCity: boolean;
  textTerms: string[];
}

export const serverFilters = (parsed: ParsedSearch | string): ServerFilters => {
  const p = typeof parsed === 'string' ? parseSearch(parsed) : parsed;
  const states = new Set<string>();
  const trades = new Set<string>();
  let laCity = false;
  const textTerms: string[] = [];
  p.terms.forEach((t) => {
    if (t.kind === 'state') {
      t.states.forEach((s) => states.add(s));
      if (t.text === 'la') laCity = true;
    } else if (t.kind === 'trade') {
      t.trades.forEach((s) => trades.add(s));
    } else {
      const clean = t.text.replace(/[^a-z0-9]/g, '');
      if (clean) textTerms.push(clean);
    }
  });
  return { states: [...states], trades: [...trades], laCity, textTerms };
};


/**
 * Human summary of how the query was understood, e.g.
 * "California · Roofing · “fontana”". Shown under the search box so the user
 * can see the system understood "CA" as California.
 */
export const describeSearch = (parsed: ParsedSearch, lang: Lang): string[] =>
  parsed.terms.map((t) => {
    if (t.kind === 'state') {
      const st = STATES.find((s) => s.code === t.states[0]);
      return st ? (lang === 'es' ? st.es : st.en) : t.text.toUpperCase();
    }
    if (t.kind === 'trade') {
      return t.trades.length === 1 ? tradeLabel(t.trades[0], lang) : `${t.text} (${t.trades.length})`;
    }
    return `“${t.text}”`;
  });
